<template>
  <div class="cities">
    <h2 class="cities__title">{{ t('title') }}</h2>
    <div v-if="fulfilled" class="cities__subtitle">
      {{ subtitle }}
    </div>
    <div v-else class="cities__subtitle">
      <SkeletonItem skeleton-class="cities__subtitle-skeleton" :is-animated="pending" />
    </div>
    <div v-if="fulfilled" class="cities__list">
      <CityCard
        v-for="city in cities"
        :key="city.id"
        v-city-view-tracker="{ city_id: city.id, name: city.title }"
        v-city-click-tracker="{ city_id: city.id, name: city.title }"
        :title="city.title"
        :excursions-count="city.excursionsCount"
        :image-link="city.image"
        :image-alt-text="city.imageAlt"
        :link="city.link"
        :country-code="city.countryCode"
      />
    </div>
    <div v-else class="cities__list">
      <CityCardSkeleton v-for="(skeleton, index) in citiesSkeletons" :key="index" :animate="pending" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { SkeletonItem } from '@experience/core-skeleton-lib';
import { useI18n } from '@experience/i18n';
import { computed, onBeforeMount, ref } from 'vue';

import { getLandingCitiesList, getStatistic, type LandingCity } from '@/shared/api/generated/api';
import { useAnalytics } from '@/shared/lib/analytics';
import { useScreenSize } from '@/shared/utils/breakpoints';

import CityCard from './CityCard.vue';
import CityCardSkeleton from './CityCardSkeleton.vue';
import ru from '../i18n/ru';

type MessageSchema = typeof ru;
const { t } = useI18n<{ message: MessageSchema }, 'ru'>({
  inheritLocale: true,
  messages: { ru },
});

const { useViewTracker, useImmediateClickTracker } = useAnalytics();

const items = ref<LandingCity[]>([]);
const citiesCount = ref(0);
const countriesCount = ref(0);
const pending = ref(false);
const fulfilled = computed(() => Boolean(items.value.length));

const { isTabletOnly } = useScreenSize();

const citiesSkeletons = computed(() => (isTabletOnly.value ? 9 : 10));
const cities = computed(() => {
  const preparedCities = items.value.map((city) => {
    return {
      id: city.id,
      title: city.name_ru,
      excursionsCount: city.excursions_count,
      image: city.image,
      imageAlt: city.name_ru,
      link: `/experience/${city.slug}/`,
      // Российским городам флаг не показываем (для RU-аудитории он избыточен),
      // пустой countryCode → CityCard не рендерит флаг.
      countryCode: city.country_code && city.country_code !== 'RU' ? city.country_code : '',
    };
  });

  // На планшете в сетку помещается только 9 городов, на десктопе показываем все.
  return isTabletOnly.value ? preparedCities.slice(0, 9) : preparedCities;
});

const subtitle = computed(() =>
  t('subtitle', {
    citiesCount: citiesCount.value,
    countriesCount: countriesCount.value,
    cities: t('cities', citiesCount.value),
    countries: t('countries', countriesCount.value),
  }),
);

const vCityViewTracker = useViewTracker('main:welcome-cities:city.view');
const vCityClickTracker = useImmediateClickTracker('main:welcome-cities:city.click');

onBeforeMount(() => fetchData());

const fetchData = async () => {
  pending.value = true;

  let citiesList;
  let statistic;

  try {
    [citiesList, statistic] = await Promise.all([getLandingCitiesList(), getStatistic()]);
  } catch {
    pending.value = false;

    return;
  }

  if (citiesList.error) {
    pending.value = false;

    return;
  }

  items.value = citiesList.data;
  citiesCount.value = statistic.data?.cities.count ?? 0;
  countriesCount.value = statistic.data?.countries.count ?? 0;
  pending.value = false;
};
</script>

<style lang="scss" scoped>
.cities {
  width: 100%;
  max-width: 1260px;
  padding: 0 var(--alias-space-20);
}

.cities__title {
  width: 100%;

  @include typography.base-800-750-extra;

  @include adaptivity.mobile {
    @include typography.base-700-500-extra;
  }
}

.cities__subtitle {
  margin-top: var(--alias-space-12);

  @include typography.base-500-400-regular;

  @include adaptivity.mobile {
    margin-top: var(--alias-space-8);

    @include typography.base-400-300-regular;
  }
}

.cities__subtitle-skeleton {
  width: 240px;
  height: var(--global-typography-line-height-400);
  border-radius: var(--alias-radius-12);

  @include adaptivity.mobile {
    width: 200px;
    height: var(--global-typography-line-height-300);
  }
}

.cities__list {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: var(--alias-space-32) var(--alias-space-8);
  align-items: center;
  justify-content: center;
  padding-top: var(--alias-space-32);

  @include adaptivity.tablet {
    grid-template-columns: repeat(3, 1fr);
    width: 100%;
    padding-top: var(--alias-space-24);
  }

  @include adaptivity.mobile {
    grid-template-columns: repeat(2, 1fr);
    column-gap: var(--alias-space-8);
  }
}

.cities__actions {
  display: flex;
  justify-content: center;
  padding-top: var(--alias-space-40);

  @include adaptivity.tablet {
    padding-top: var(--alias-space-32);
  }

  @include adaptivity.mobile {
    padding-top: var(--alias-space-24);
  }
}

.cities__select-city-button {
  @include adaptivity.tablet {
    width: 100%;
  }
}
</style>
